import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authManager, userService, tokenManager, subscriptionService, profileManager, biometricService } from '../services';
import API_CONFIG from '../config/api';
import { isCapacitor, isElectron, getPostLoginDestination } from '../utils/platform';
import { openExternalUrl } from '../utils/browserHelper';
import Button from '../components/buttons/Button';
import Loader from '../components/loaders/Loader';
import { useLoading } from '../context/LoadingContext';
import { useAuth } from '../context/AuthContext';
import { createLogger } from '../services/Debug';
import {
  FaLock,
  FaUser,
  FaEnvelope,
  FaArrowRight,
  FaArrowLeft
} from 'react-icons/fa';
import './AuthFlow.css';
import TrustDeviceModal from '../components/TrustDeviceModal';
import { useKeyboardAware } from '../hooks/useKeyboardAware';
import { SPECIALTIES } from '../constants/specialties';

// Helper to keep only user-friendly error messages (filter technical ones)
function getUserFacingError(err, fallbackMessage) {
  try {
    const raw = err?.response?.data?.error || err?.response?.data?.message || '';
    const text = String(raw || '').trim();
    if (!text) return fallbackMessage;
    const lower = text.toLowerCase();
    const isTechnical = (
      lower.includes('http error') ||
      lower.includes('failed to fetch') ||
      lower.includes('network request failed') ||
      lower.includes('unknown error') ||
      lower.includes('refresh') ||
      lower.includes('token')
    );
    return isTechnical ? fallbackMessage : text;
  } catch (_) {
    return fallbackMessage;
  }
}

const FLOW_STEPS = {
  PHONE: 'phone',
  VERIFY_OTP: 'verify_otp',
  SET_PIN: 'set_pin',        // Keep for returning users without PIN
  ENTER_PIN: 'enter_pin',    // Keep for existing users with PIN
  REGISTER: 'register',      // NEW: Unified registration (PIN + email + profile) for new users
  SET_EMAIL: 'set_email',    // Deprecated: kept for backwards compatibility
  PROFILE: 'profile',        // Deprecated: kept for backwards compatibility
  RECOVER_PIN: 'recover_pin',
  RESET_PIN: 'reset_pin'
};

function AuthFlow() {
  const log = createLogger('AuthFlow');
  const navigate = useNavigate();
  const location = useLocation();
  const { setLoading: setGlobalLoading } = useLoading();
  const { isAuthenticated } = useAuth();
  const [currentStep, setCurrentStep] = useState(FLOW_STEPS.PHONE);
  const [flowData, setFlowData] = useState({
    phone: '',
    code: '',
    pin: '',
    email: '',
    firstName: '',
    lastName: '',
    title: '',
    specialty: '',
    subscriptionPlan: '',
    isNewUser: false,
    hasPin: false,
    isReturningUser: false,
    recoveryToken: null,
    channel: 'sms', // 'sms' or 'whatsapp'
    rememberMe: true
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stepDescription, setStepDescription] = useState(null);
  const [stepTitle, setStepTitle] = useState(null);
  const [showTrustModal, setShowTrustModal] = useState(false);
  const [trustContext, setTrustContext] = useState({ phone: '', pin: '' });
  const trustResolverRef = React.useRef(null);
  const [biometricReady, setBiometricReady] = useState(false);
  const [bioLoginLoading, setBioLoginLoading] = useState(false);
  const [biometricType, setBiometricType] = useState('biométrie');

  // Keyboard awareness for mobile (only lifts for profile fields)
  const { keyboardHeight, shouldLift } = useKeyboardAware();

  // Handle URL parameters for PIN recovery
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');

    if (token) {
      // PIN recovery token provided
      setFlowData(prev => ({ ...prev, recoveryToken: token }));
      setCurrentStep(FLOW_STEPS.RESET_PIN);
      // Clean URL
      navigate('/auth', { replace: true });
    }
  }, [location.search, navigate]);

  // Update step title and description
  useEffect(() => {
    switch (currentStep) {
      case FLOW_STEPS.PHONE:
        setStepTitle("Bienvenue");
        setStepDescription("Entrez votre numéro de téléphone pour commencer ou vous connecter.");
        break;
      case FLOW_STEPS.VERIFY_OTP:
        setStepTitle("Vérification");
        if (flowData.isNewUser) {
          setStepDescription("Vérifions votre numéro - il vous servira d'accès à votre compte.");
        } else {
          setStepDescription("Entrez le code de vérification envoyé à votre téléphone.");
        }
        break;
      case FLOW_STEPS.SET_PIN:
        if (flowData.isReturningUser) {
          setStepTitle("Bon retour!");
          setStepDescription("Configurez votre code PIN pour sécuriser votre compte.");
        } else {
          setStepTitle("Créer un code PIN");
          setStepDescription("Choisissez un code PIN à 6 chiffres pour sécuriser votre compte.");
        }
        break;
      case FLOW_STEPS.ENTER_PIN:
        setStepTitle("Connexion");
        setStepDescription("Entrez votre code PIN pour vous connecter.");
        break;
      case FLOW_STEPS.REGISTER:
        setStepTitle("Créer votre compte");
        setStepDescription("Configurez votre PIN et complétez votre profil.");
        break;
      case FLOW_STEPS.SET_EMAIL:
        setStepTitle("Email");
        setStepDescription("Ajoutez votre email");
        break;
      case FLOW_STEPS.PROFILE:
        setStepTitle("Profil");
        setStepDescription("Complétez votre profil pour personnaliser votre expérience.");
        break;
      case FLOW_STEPS.SUBSCRIPTION:
        break;
      case FLOW_STEPS.RECOVER_PIN:
        setStepTitle("Récupération du PIN");
        setStepDescription("Entrez votre email pour recevoir un lien de réinitialisation.");
        break;
      case FLOW_STEPS.RESET_PIN:
        setStepTitle("Nouveau PIN");
        setStepDescription("Choisissez un nouveau code PIN.");
        break;
      default:
        break;
    }
  }, [currentStep, flowData.isReturningUser, flowData.isNewUser]);

  // Check if already authenticated
  useEffect(() => {
    if (isAuthenticated && currentStep === FLOW_STEPS.PHONE) {
      navigate(getPostLoginDestination());
    }
  }, [isAuthenticated, currentStep, navigate]);

  // Detect biometric credentials for quick-login (mobile)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!isCapacitor) return;
        const hasCredentials = await biometricService.hasStoredCredentials();
        if (mounted) {
          setBiometricReady(!!hasCredentials);

          // Get the biometric type to display in the button
          if (hasCredentials) {
            const type = await biometricService.getBiometricType();
            if (type && mounted) {
              setBiometricType(type);
            }
          }
        }
      } catch (_) {}
    })();
    return () => { mounted = false; };
  }, []);

  const handleBiometricLogin = async () => {
    setError(null);
    setBioLoginLoading(true);
    try {
      await biometricService.loginWithBiometricDevice();
      // On success, navigate to platform-specific destination
      setTimeout(() => navigate(getPostLoginDestination()), 50);
    } catch (err) {
      setError(getUserFacingError(err, "Impossible d'utiliser la connexion biométrique"));
    } finally {
      setBioLoginLoading(false);
    }
  };

  // Handle trust device modal
  const requestTrustIfNeeded = async (phone, pin) => {
    if (!isCapacitor) return;

    // Check if device already has stored credentials
    try {
      const hasCredentials = await biometricService.hasStoredCredentials();
      if (hasCredentials) {
        // Device already enrolled, no need to show modal
        log.debug('Device already has stored credentials, skipping trust modal');
        return;
      }
    } catch (err) {
      log.warn('Error checking stored credentials:', err);
      // Continue to show modal if check fails
    }

    // Show modal only for devices without credentials
    return new Promise((resolve) => {
      trustResolverRef.current = resolve;
      setTrustContext({ phone, pin });
      setShowTrustModal(true);
    });
  };

  const handleTrustResponse = (shouldTrust) => {
    setShowTrustModal(false);
    if (trustResolverRef.current) {
      trustResolverRef.current(shouldTrust);
      trustResolverRef.current = null;
    }
  };

  // Handle step transitions
  const handleStepComplete = (stepData) => {
    setFlowData(prev => ({ ...prev, ...stepData }));
    setError(null);

    // Determine next step
    if (stepData.restart) {
      // Reset the flow to the beginning
      setCurrentStep(FLOW_STEPS.PHONE);
      setFlowData({
        phone: '',
        code: '',
        pin: '',
        email: '',
        firstName: '',
        lastName: '',
        title: '',
        specialty: '',
        subscriptionPlan: '',
        isNewUser: false,
        hasPin: false,
        isReturningUser: false,
        recoveryToken: null,
        channel: 'sms',
        rememberMe: true
      });
      return;
    }
    if (stepData.goToOtp) {
      setCurrentStep(FLOW_STEPS.VERIFY_OTP);
    } else if (stepData.goToRegister) {
      // New users and returning users (no PIN) go to unified registration
      setCurrentStep(FLOW_STEPS.REGISTER);
    } else if (stepData.goToPin) {
      // Determine PIN step based on user status
      if (stepData.isNewUser || stepData.isReturningUser || (stepData.userExists && !stepData.hasPin)) {
        setCurrentStep(FLOW_STEPS.SET_PIN);
      } else if (stepData.userExists && stepData.hasPin) {
        setCurrentStep(FLOW_STEPS.ENTER_PIN);
      } else {
        // Fallback based on flowData
        if (flowData.isNewUser || flowData.isReturningUser || !flowData.hasPin) {
          setCurrentStep(FLOW_STEPS.SET_PIN);
        } else {
          setCurrentStep(FLOW_STEPS.ENTER_PIN);
        }
      }
    } else if (stepData.goToEmail) {
      setCurrentStep(FLOW_STEPS.SET_EMAIL);
    } else if (stepData.goToProfile) {
      setCurrentStep(FLOW_STEPS.PROFILE);
    } else if (stepData.goToSubscription) {
      // Removed subscription step; finalize flow
      setTimeout(() => navigate(getPostLoginDestination()), 100);
    } else if (stepData.goToWelcome) {
      // Removed welcome step; finalize flow
      setTimeout(() => navigate(getPostLoginDestination()), 100);
    } else if (stepData.goToRecover) {
      setCurrentStep(FLOW_STEPS.RECOVER_PIN);
    } else if (stepData.isComplete) {
      // Authentication complete, navigate to subscription-required with robust HashRouter fallback
      // 150ms delay to ensure React state updates are flushed before navigation (fixes iOS race condition)
      setTimeout(() => {
        try {
          navigate('/subscription-required', { replace: true });
          if (typeof window !== 'undefined' && window.location && !/\/#\/subscription-required$/.test(window.location.hash)) {
            window.location.hash = '#/subscription-required';
          }
        } catch (_) {
          if (typeof window !== 'undefined') {
            window.location.hash = '#/subscription-required';
          }
        }
      }, 150);
    }
  };

  // Render current step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case FLOW_STEPS.PHONE:
        return (
          <PhoneStep
            data={flowData}
            onComplete={handleStepComplete}
            setError={setError}
          />
        );
      case FLOW_STEPS.VERIFY_OTP:
        return (
          <OTPStep
            data={flowData}
            onComplete={handleStepComplete}
            onBack={() => setCurrentStep(FLOW_STEPS.PHONE)}
            setError={setError}
          />
        );
      case FLOW_STEPS.SET_PIN:
        return (
          <SetPinStep
            data={flowData}
            onComplete={handleStepComplete}
            setError={setError}
          />
        );
      case FLOW_STEPS.ENTER_PIN:
        return (
          <EnterPinStep
            data={flowData}
            onComplete={handleStepComplete}
            onBack={() => setCurrentStep(FLOW_STEPS.VERIFY_OTP)}
            setError={setError}
            requestTrustIfNeeded={requestTrustIfNeeded}
          />
        );
      case FLOW_STEPS.REGISTER:
        return (
          <UnifiedRegistrationStep
            data={flowData}
            onComplete={handleStepComplete}
            setError={setError}
          />
        );
      case FLOW_STEPS.SET_EMAIL:
        return (
          <SetEmailStep
            data={flowData}
            onComplete={handleStepComplete}
            onSkip={() => handleStepComplete({ goToProfile: true })}
            setError={setError}
          />
        );
      case FLOW_STEPS.PROFILE:
        return (
          <ProfileStep
            data={flowData}
            onComplete={handleStepComplete}
            setError={setError}
          />
        );
      case FLOW_STEPS.SUBSCRIPTION:
        return null;
      case FLOW_STEPS.RECOVER_PIN:
        return (
          <RecoverPinStep
            data={flowData}
            onComplete={handleStepComplete}
            onBack={() => setCurrentStep(FLOW_STEPS.PHONE)}
            setError={setError}
          />
        );
      case FLOW_STEPS.RESET_PIN:
        return (
          <ResetPinStep
            data={flowData}
            onComplete={handleStepComplete}
            setError={setError}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="auth-flow-container"
      style={{
        transform: shouldLift ? `translateY(-${keyboardHeight * 0.4}px)` : 'none'
      }}
    >
      <div className="auth-flow">
        <div className="auth-flow-header">
          <img src={`${process.env.PUBLIC_URL}/icon.png`} alt="voxia" className="auth-logo" />
          <h1 className="auth-title">{stepTitle}</h1>
          {stepDescription && (
            <p className="step-description">{stepDescription}</p>
          )}
        </div>

        {error && (
          <div className="auth-error">
            {error}
          </div>
        )}

        <div className="auth-step-content">
          {isLoading ? (
            <div className="auth-loading">
              <Loader />
            </div>
          ) : (
            renderCurrentStep()
          )}
        </div>
        {isCapacitor && biometricReady && currentStep === FLOW_STEPS.PHONE && (
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="secondary"
              onClick={handleBiometricLogin}
              disabled={bioLoginLoading}
            >
              {bioLoginLoading ? 'Connexion…' : `Se connecter avec ${biometricType}`}
            </Button>
          </div>
        )}

      </div>

      {showTrustModal && (
        <TrustDeviceModal
          onTrust={() => handleTrustResponse(true)}
          onSkip={() => handleTrustResponse(false)}
        />
      )}
    </div>
  );
}

// Phone input step
function PhoneStep({ data, onComplete, setError }) {
  const [country, setCountry] = useState('CH'); // Default to Switzerland
  const [phone, setPhone] = useState(data.phone || '');
  const [channel, setChannel] = useState(data.channel || 'sms');
  const [email, setEmail] = useState('');
  const [useEmail, setUseEmail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rememberMe, setRememberMe] = useState(data.rememberMe !== undefined ? data.rememberMe : true);
  const stepLog = createLogger('AuthFlow.PhoneStep');

  const countries = {
    CH: { code: '+41', flag: '🇨🇭', name: 'Suisse', placeholder: '79 123 45 67' },
    FR: { code: '+33', flag: '🇫🇷', name: 'France', placeholder: '6 12 34 56 78' },
    DE: { code: '+49', flag: '🇩🇪', name: 'Allemagne', placeholder: '151 2345 6789' },
    BE: { code: '+32', flag: '🇧🇪', name: 'Belgique', placeholder: '470 12 34 56' }
  };

  const formatPhoneNumber = (value, countryCode) => {
    // Remove all non-digits except the leading +
    const cleaned = value.replace(/[^\d+]/g, '');

    // Remove country code if present at the beginning
    let localNumber = cleaned;
    if (cleaned.startsWith('+41')) {
      localNumber = cleaned.substring(3);
    } else if (cleaned.startsWith('+33')) {
      localNumber = cleaned.substring(3);
    } else if (cleaned.startsWith('+49')) {
      localNumber = cleaned.substring(3);
    } else if (cleaned.startsWith('+32')) {
      localNumber = cleaned.substring(3);
    } else if (cleaned.startsWith('41')) {
      localNumber = cleaned.substring(2);
    } else if (cleaned.startsWith('33')) {
      localNumber = cleaned.substring(2);
    } else if (cleaned.startsWith('49')) {
      localNumber = cleaned.substring(2);
    } else if (cleaned.startsWith('32')) {
      localNumber = cleaned.substring(2);
    } else if (cleaned.startsWith('0')) {
      localNumber = cleaned.substring(1);
    } else if (cleaned.startsWith('+')) {
      return value; // Unknown country code, return as-is
    }

    // Format based on country
    switch (countryCode) {
      case 'CH':
        // Swiss format: 79 123 45 67
        if (localNumber.length <= 2) return localNumber;
        if (localNumber.length <= 5) return `${localNumber.substring(0, 2)} ${localNumber.substring(2)}`;
        if (localNumber.length <= 7) return `${localNumber.substring(0, 2)} ${localNumber.substring(2, 5)} ${localNumber.substring(5)}`;
        return `${localNumber.substring(0, 2)} ${localNumber.substring(2, 5)} ${localNumber.substring(5, 7)} ${localNumber.substring(7, 9)}`;

      case 'FR':
        // French format: 6 12 34 56 78
        if (localNumber.length <= 1) return localNumber;
        if (localNumber.length <= 3) return `${localNumber[0]} ${localNumber.substring(1)}`;
        if (localNumber.length <= 5) return `${localNumber[0]} ${localNumber.substring(1, 3)} ${localNumber.substring(3)}`;
        if (localNumber.length <= 7) return `${localNumber[0]} ${localNumber.substring(1, 3)} ${localNumber.substring(3, 5)} ${localNumber.substring(5)}`;
        return `${localNumber[0]} ${localNumber.substring(1, 3)} ${localNumber.substring(3, 5)} ${localNumber.substring(5, 7)} ${localNumber.substring(7, 9)}`;

      case 'DE':
        // German format: 151 2345 6789
        if (localNumber.length <= 3) return localNumber;
        if (localNumber.length <= 7) return `${localNumber.substring(0, 3)} ${localNumber.substring(3)}`;
        return `${localNumber.substring(0, 3)} ${localNumber.substring(3, 7)} ${localNumber.substring(7, 11)}`;

      case 'BE':
        // Belgian mobile format example: 470 12 34 56
        if (localNumber.length <= 3) return localNumber;
        if (localNumber.length <= 5) return `${localNumber.substring(0, 3)} ${localNumber.substring(3)}`;
        if (localNumber.length <= 7) return `${localNumber.substring(0, 3)} ${localNumber.substring(3, 5)} ${localNumber.substring(5)}`;
        return `${localNumber.substring(0, 3)} ${localNumber.substring(3, 5)} ${localNumber.substring(5, 7)} ${localNumber.substring(7, 9)}`;

      default:
        return localNumber;
    }
  };

  const getE164Format = (phoneNumber, countryCode) => {
    const cleaned = phoneNumber.replace(/\D/g, '');
    const countryPrefix = countries[countryCode].code.replace('+', '');

    // If already has country code
    if (cleaned.startsWith(countryPrefix)) {
      return `+${cleaned}`;
    }

    // If starts with 0, remove it
    if (cleaned.startsWith('0')) {
      return `${countries[countryCode].code}${cleaned.substring(1)}`;
    }

    // Otherwise, add country code
    return `${countries[countryCode].code}${cleaned}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    stepLog.info('Submitting start', { mode: useEmail ? 'email' : 'phone', rememberMe, country, channel });

    if (useEmail) {
      // Email mode validation
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setError('Veuillez entrer une adresse email valide');
        return;
      }
    } else {
      // Phone mode validation
      const minLength = (country === 'CH' || country === 'FR' || country === 'BE') ? 9 : 10;
      if (!phone || phone.replace(/\D/g, '').length < minLength) {
        setError('Veuillez entrer un numéro de téléphone valide');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      if (useEmail) {
        // Email flow: send email code and proceed to OTP
        stepLog.debug('startEmailLogin()', { rememberMe });
        await authManager.startEmailLogin(email);
        onComplete({
          email,
          useEmailLogin: true,
          rememberMe,
          goToOtp: true
        });
        stepLog.info('Email code sent, moving to OTP');
      } else {
        const e164Phone = getE164Format(phone, country);

        // Check if this is a test account (Swiss number ending with 000 00 00, any prefix like 79, 78, 77, etc.)
        const cleanedPhone = phone.replace(/\D/g, '');
        const isTestAccount = country === 'CH' && /^\d{2}0000000$/.test(cleanedPhone);

        if (isTestAccount) {
          // Test account: skip server call and proceed directly to OTP
          stepLog.info('Test account detected, skipping server call');
          onComplete({
            phone: e164Phone, // Use the actual test phone number entered
            channel,
            isNewUser: false,
            hasPin: true,
            rememberMe,
            isTestAccount: true,
            goToOtp: true
          });
        } else {
          // Single call to phone/start that returns user status
          stepLog.debug('startPhoneAuth()', { rememberMe, channel });
          const response = await authManager.startPhoneAuth(e164Phone, channel);

          // Determine flow based on response
          if (response.userExists && response.hasPin) {
            // Existing user with PIN → Login flow
            onComplete({
              phone: e164Phone,
              channel,
              isNewUser: false,
              hasPin: true,
              rememberMe,
              goToOtp: true
            });
            stepLog.info('Existing user with PIN → OTP');
          } else if (response.userExists && !response.hasPin) {
            // Existing user without PIN → Welcome back + Set PIN
            onComplete({
              phone: e164Phone,
              channel,
              isNewUser: false,
              hasPin: false,
              isReturningUser: true,
              rememberMe,
              goToOtp: true
            });
            stepLog.info('Returning user without PIN → OTP');
          } else {
            // New user → Registration flow
            onComplete({
              phone: e164Phone,
              channel,
              isNewUser: true,
              hasPin: false,
              rememberMe,
              goToOtp: true
            });
            stepLog.info('New user → OTP');
          }
        }
      }
    } catch (err) {
      setError(getUserFacingError(err, 'Erreur lors de l\'envoi du code'));
      stepLog.error('Submission failed', { message: err?.message });
    } finally {
      setIsSubmitting(false);
      stepLog.debug('Submitting end');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="auth-step-form">
      {!useEmail ? (
        <div className="form-group-auth">
          <label htmlFor="phone">Numéro de téléphone</label>
          <div className="phone-input-container">
            <div className="country-selector">
              <select
                value={country}
                onChange={(e) => {
                  setCountry(e.target.value);
                  setPhone(''); // Clear phone when country changes
                }}
                className="country-select"
              >
                {Object.entries(countries).map(([code, info]) => (
                  <option key={code} value={code}>
                    {info.flag} {info.code}
                  </option>
                ))}
              </select>
            </div>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(formatPhoneNumber(e.target.value, country))}
              placeholder={countries[country].placeholder}
              className="auth-input phone-input"
              autoComplete="tel"
              autoFocus
            />
          </div>
        </div>
      ) : (
        <div className="form-group-auth">
          <label htmlFor="email">Connexion par email</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="votre@email.com"
            className="auth-input"
            autoComplete="email"
            autoFocus
          />
          <small>Nécessite un compte</small>
        </div>
      )}
      {!isCapacitor && !isElectron && (
        <div className="form-group-auth" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            id="rememberMe"
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => {
              setRememberMe(e.target.checked);
              stepLog.debug('rememberMe toggled', { checked: e.target.checked });
            }}
          />
          <label htmlFor="rememberMe" style={{ margin: 0 }}>Rester connecté</label>
        </div>
      )}
<div className="auth-links" style={{ marginTop: '8px', textAlign: 'center' }}>

        <Button
          variant="secondary"
          onClick={() => setUseEmail(!useEmail)}
        >
          {useEmail ? 'Se connecter via téléphone' : "Se connecter avec l'email"}
        </Button>
        <Button
        icon={FaArrowRight}
        htmlType="submit"
        variant="primary"
        disabled={isSubmitting}
      >
        Continuer
      </Button>
      </div>
    </form>
  );
}

// OTP verification step
function OTPStep({ data, onComplete, onBack, setError }) {
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!code || code.length !== 6) {
      setError('Veuillez entrer le code à 6 chiffres');
      return;
    }

    setIsSubmitting(true);
    try {
      if (data.isTestAccount) {
        // Test account: skip verification and proceed directly to PIN
        onComplete({
          code,
          hasPin: true,
          userExists: true,
          isNewUser: false,
          isReturningUser: false,
          isTestAccount: true,
          goToPin: true
        });
      } else if (data.useEmailLogin) {
        // Email login flow: do NOT call API here; proceed to PIN entry
        onComplete({
          code,
          hasPin: true,
          userExists: true,
          isNewUser: false,
          isReturningUser: false,
          goToPin: true
        });
      } else if (data.isNewUser || data.isReturningUser) {
        // Phone flow: verify phone first
        const response = await authManager.verifyPhoneCode(data.phone, code);
        // Route to unified registration for new users and returning users
        onComplete({
          code,
          hasPin: response.user?.has_pin || false,
          userExists: !data.isNewUser,
          isNewUser: data.isNewUser,
          isReturningUser: data.isReturningUser,
          goToRegister: true  // Use unified registration flow
        });
      } else {
        // Existing user with PIN - will send code+PIN together for security
        onComplete({
          code,
          hasPin: data.hasPin,
          userExists: true,
          isNewUser: false,
          isReturningUser: false,
          goToPin: true
        });
      }
    } catch (err) {
      setError(getUserFacingError(err, 'Code incorrect'));
      // Restart the flow from the beginning on error
      onComplete({ restart: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="auth-step-form">
      <div className="form-group-auth">
        <label htmlFor="code">Code de vérification</label>
        <input
          id="code"
          type="text"
          inputMode="numeric"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          className="auth-input auth-input-code"
          autoComplete="one-time-code"
          autoFocus
          maxLength={6}
        />
      </div>

      <div className="form-actions">
        <Button
          icon={FaArrowLeft}
          variant="secondary"
          onClick={onBack}
          disabled={isSubmitting}
        >
          Retour
        </Button>
        <Button
          icon={FaArrowRight}
          htmlType="submit"
          variant="primary"
          disabled={isSubmitting}
        >
          Vérifier
        </Button>
      </div>

    </form>
  );
}

// Set PIN step (new users)
function SetPinStep({ data, onComplete, setError }) {
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const confirmPinRef = React.useRef(null);

  // Auto-focus confirm PIN when first PIN is complete
  useEffect(() => {
    if (pin.length === 6 && confirmPinRef.current) {
      confirmPinRef.current.focus();
    }
  }, [pin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!pin || pin.length !== 6) {
      setError('Le code PIN doit contenir 6 chiffres');
      return;
    }

    if (pin !== confirmPin) {
      setError('Les codes PIN ne correspondent pas');
      return;
    }

    // Prevent trivial PINs
    const trivial = new Set(['000000','111111','222222','333333','444444','555555','666666','777777','888888','999999','123456','654321']);
    if (trivial.has(pin)) {
      setError('Le code PIN est trop simple. Veuillez choisir un autre code.');
      return;
    }

    setIsSubmitting(true);
    try {
      const resp = await authManager.setPin(data.phone, pin);

      // Determine next step based on user type
      if (data.isNewUser) {
        // New user → go to email step
        onComplete({
          pin,
          goToEmail: true
        });
      } else if (data.isReturningUser) {
        // Returning user without PIN → finish auth; email/profile checks happen after completeLogin only
        // Persist remember-me choice on web after successful authentication
        if (!isCapacitor && !isElectron) {
          try {
            const remember = data.rememberMe !== false;
            if (remember) {
              localStorage.setItem('rememberMe', '1');
              localStorage.removeItem('sessionExpiryAt');
            } else {
              localStorage.setItem('rememberMe', '0');
            localStorage.setItem('sessionExpiryAt', String(Date.now() + 12 * 60 * 60 * 1000));
            }
          } catch (_) {}
        }

        onComplete({ pin, isComplete: true });
      } else {
        // Fallback to email step
        onComplete({
          pin,
          goToEmail: true
        });
      }
    } catch (err) {
      setError(getUserFacingError(err, 'Erreur lors de la création du PIN'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="auth-step-form">
      <div className="form-group-auth">
        <label htmlFor="pin">Code PIN (6 chiffres)</label>
        <input
          id="pin"
          type="password"
          inputMode="numeric"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="••••••"
          className="auth-input auth-input-pin"
          autoComplete="new-password"
          autoFocus
          maxLength={6}
        />
      </div>

      <div className="form-group-auth">
        <label htmlFor="confirmPin">Confirmer le code PIN</label>
        <input
          ref={confirmPinRef}
          id="confirmPin"
          type="password"
          inputMode="numeric"
          value={confirmPin}
          onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="••••••"
          className="auth-input auth-input-pin"
          autoComplete="new-password"
          maxLength={6}
        />
      </div>

      <Button
        icon={FaArrowRight}
        htmlType="submit"
        variant="primary"
        size="large"
        disabled={isSubmitting}
      >
        Continuer
      </Button>
    </form>
  );
}

// Enter PIN step (existing users)
function EnterPinStep({ data, onComplete, onBack, setError, requestTrustIfNeeded }) {
  const [pin, setPin] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!pin || pin.length !== 6) {
      setError('Veuillez entrer votre code PIN à 6 chiffres');
      return;
    }

    setIsSubmitting(true);
    try {
      let response;

      if (data.isTestAccount) {
        // Test account: use test login endpoint
        response = await authManager.testLogin(data.phone, data.code, pin);
      } else if (data.useEmailLogin) {
        // Email login flow
        response = await authManager.completeEmailLogin(data.email, data.code, pin);
      } else {
        // Regular phone login flow
        response = await authManager.completeLogin(data.phone, data.code, pin);
      }

      // Try to enroll device as trusted if on mobile (skip for test account)
      if (isCapacitor && !data.isTestAccount) {
        try {
          await requestTrustIfNeeded(data.phone, pin);
        } catch (_) {
          // Device trust is optional
        }
      }


      const hasProfile = response?.hasProfile;


      // Persist remember-me choice on web after successful authentication
      if (!isCapacitor && !isElectron) {
        try {
          const remember = data.rememberMe !== false;
          if (remember) {
            localStorage.setItem('rememberMe', '1');
            localStorage.removeItem('sessionExpiryAt');
          } else {
            localStorage.setItem('rememberMe', '0');
            localStorage.setItem('sessionExpiryAt', String(Date.now() + 12 * 60 * 60 * 1000));

          }
        } catch (_) {}
      }

      if (!hasProfile) {
        onComplete({ goToProfile: true });
      } else {
        // Direct redirect to home on successful login
        onComplete({ isComplete: true });
      }
    } catch (err) {
      // Prefer sanitized backend error message when available
      let errorMessage = getUserFacingError(err, null);
      if (!errorMessage) {
        // Fall back to generic message for security
        errorMessage = 'Code de vérification ou pin incorrect';

        // Check for account lockout with retry time
        const errorData = err.response?.data;
        if (errorData?.retryAfterSeconds || errorData?.retryAt) {
          const seconds = errorData.retryAfterSeconds;
          if (seconds) {
            const minutes = Math.ceil(seconds / 60);
            errorMessage += `. Compte verrouillé. Réessayez dans ${minutes} minute${minutes > 1 ? 's' : ''}`;
          } else if (errorData.retryAt) {
            // Parse the ISO date and calculate remaining time
            const retryTime = new Date(errorData.retryAt);
            const now = new Date();
            const remainingMs = retryTime - now;
            if (remainingMs > 0) {
              const minutes = Math.ceil(remainingMs / 60000);
              errorMessage += `. Compte verrouillé. Réessayez dans ${minutes} minute${minutes > 1 ? 's' : ''}`;
            }
          }
        }
      }

      setError(errorMessage);
      // Restart the flow from the beginning on error
      onComplete({ restart: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="auth-step-form">
      <div className="form-group-auth">
        <label htmlFor="pin">Code PIN</label>
        <input
          id="pin"
          type="password"
          inputMode="numeric"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="••••••"
          className="auth-input auth-input-pin"
          autoComplete="current-password"
          autoFocus
          maxLength={6}
        />
      </div>

      <div className="form-actions">
        <Button
          icon={FaArrowLeft}
          variant="secondary"
          onClick={onBack}
          disabled={isSubmitting}
        >
          Retour
        </Button>
        <Button
          icon={FaLock}
          htmlType="submit"
          variant="primary"
          disabled={isSubmitting}
        >
          Se connecter
        </Button>
      </div>

      <div className="auth-links-pin" style={{ marginTop: '20px', textAlign: 'center' }}>
        <button className="forgot-pin-button"
          
          onClick={() => onComplete({ goToRecover: true })}
        >
          PIN oublié?
        </button>
      </div>
    </form>
  );
}

// Set email step (optional)
function SetEmailStep({ data, onComplete, onSkip, setError }) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!email) {
      onSkip();
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }

    setIsSubmitting(true);
    try {
      await authManager.setEmail(data.phone, email);
      onComplete({
        email,
        goToProfile: true
      });
    } catch (err) {
      setError(getUserFacingError(err, 'Erreur lors de l\'ajout de l\'email'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="auth-step-form">
      <div className="form-group-auth">
        <label htmlFor="email">Adresse email</label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="votre@email.com"
          className="auth-input"
          autoComplete="email"
          autoFocus
        />
        <small>Un email de vérification sera envoyé</small>
      </div>

      <div className="form-actions">
        <Button
          icon={FaEnvelope}
          htmlType="submit"
          variant="primary"
          disabled={isSubmitting}
        >
          Ajouter l'email
        </Button>
      </div>
    </form>
  );
}

// Profile completion step
function ProfileStep({ data, onComplete, setError }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [title, setTitle] = useState('');
  const [specialty, setSpecialty] = useState('médecine générale');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!firstName || !lastName) {
      setError('Veuillez entrer votre nom et prénom');
      return;
    }

    setIsSubmitting(true);
    try {
      await authManager.completeProfile(firstName, lastName, title, specialty);
      onComplete({
        firstName,
        lastName,
        title,
        specialty,
        isComplete: true
      });
    } catch (err) {
      setError(getUserFacingError(err, 'Erreur lors de la création du profil'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="auth-step-form">
      <div className="form-row">
        <div className="form-group-auth">
          <label htmlFor="title">Titre</label>
          <select
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="auth-input"
          >
            <option value="">Sélectionner</option>
            <option value="Dr">Dr</option>
            <option value="Pr">Pr</option>
            <option value="M.">M.</option>
            <option value="Mme">Mme</option>
          </select>
        </div>

        <div className="form-group-auth">
          <label htmlFor="firstName">Prénom</label>
          <input
            id="firstName"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Jean"
            className="auth-input"
            autoComplete="given-name"
            required
          />
        </div>
      </div>

      <div className="form-group-auth">
        <label htmlFor="lastName">Nom</label>
        <input
          id="lastName"
          type="text"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Dupont"
          className="auth-input"
          autoComplete="family-name"
          required
        />
      </div>

      <div className="form-group-auth">
        <label htmlFor="specialty">Spécialité</label>
        <select
          id="specialty"
          value={specialty}
          onChange={(e) => setSpecialty(e.target.value)}
          className="auth-input"
        >
          <option value="">Sélectionner</option>
          {SPECIALTIES.map((spec, index) => (
            <option key={index} value={spec}>{spec}</option>
          ))}
        </select>
      </div>

      <Button
        icon={FaUser}
        htmlType="submit"
        variant="primary"
        size="large"
        disabled={isSubmitting}
      >
        Continuer
      </Button>
    </form>
  );
}

// Removed Subscription and Welcome steps

// Unified registration step (new users: PIN → Email → Profile, single API call at the end)
function UnifiedRegistrationStep({ data, onComplete, setError }) {
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [title, setTitle] = useState('');
  const [specialty, setSpecialty] = useState('médecine générale');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState(1); // 1: PIN, 2: Email, 3: Profile
  const confirmPinRef = React.useRef(null);

  // Auto-focus confirm PIN
  useEffect(() => {
    if (pin.length === 6 && confirmPinRef.current) {
      confirmPinRef.current.focus();
    }
  }, [pin]);

  const handlePinSubmit = (e) => {
    e.preventDefault();
    setError(null);

    if (!pin || pin.length !== 6) {
      setError('Le code PIN doit contenir 6 chiffres');
      return;
    }
    if (pin !== confirmPin) {
      setError('Les codes PIN ne correspondent pas');
      return;
    }

    const trivial = new Set(['000000','111111','222222','333333','444444','555555','666666','777777','888888','999999','123456','654321']);
    if (trivial.has(pin)) {
      setError('Le code PIN est trop simple. Veuillez choisir un autre code.');
      return;
    }

    setStep(2); // Move to email step
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    setError(null);

    // Email is optional, but if provided must be valid
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }

    setStep(3); // Move to profile step
  };

  const handleSkipEmail = () => {
    setEmail('');
    setStep(3);
  };

  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!firstName || !lastName) {
      setError('Veuillez entrer votre nom et prénom');
      return;
    }

    setIsSubmitting(true);
    try {
      await authManager.registerUnified(
        data.phone,
        pin,
        firstName,
        lastName,
        email || null,
        title || null,
        specialty || null
      );

      // Persist remember-me choice
      if (!isCapacitor && !isElectron) {
        try {
          const remember = data.rememberMe !== false;
          if (remember) {
            localStorage.setItem('rememberMe', '1');
            localStorage.removeItem('sessionExpiryAt');
          } else {
            localStorage.setItem('rememberMe', '0');
            localStorage.setItem('sessionExpiryAt', String(Date.now() + 12 * 60 * 60 * 1000));
          }
        } catch (_) {}
      }

      onComplete({ isComplete: true });
    } catch (err) {
      setError(getUserFacingError(err, 'Erreur lors de l\'inscription'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 1: PIN entry
  if (step === 1) {
    return (
      <form onSubmit={handlePinSubmit} className="auth-step-form">
        <div className="form-group-auth">
          <label htmlFor="pin">Code PIN (6 chiffres)</label>
          <input
            id="pin"
            type="password"
            inputMode="numeric"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="••••••"
            className="auth-input auth-input-pin"
            autoComplete="new-password"
            autoFocus
            maxLength={6}
          />
        </div>

        <div className="form-group-auth">
          <label htmlFor="confirmPin">Confirmer le code PIN</label>
          <input
            ref={confirmPinRef}
            id="confirmPin"
            type="password"
            inputMode="numeric"
            value={confirmPin}
            onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="••••••"
            className="auth-input auth-input-pin"
            autoComplete="new-password"
            maxLength={6}
          />
        </div>

        <Button icon={FaArrowRight} htmlType="submit" variant="primary" size="large">
          Continuer
        </Button>
      </form>
    );
  }

  // Step 2: Email (optional)
  if (step === 2) {
    return (
      <form onSubmit={handleEmailSubmit} className="auth-step-form">
        <div className="form-group-auth">
          <label htmlFor="email">Adresse email</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="votre@email.com"
            className="auth-input"
            autoComplete="email"
            autoFocus
          />
          <small>Un email de vérification sera envoyé</small>
        </div>

        <div className="form-actions">
          <Button
            icon={FaEnvelope}
            htmlType="submit"
            variant="primary"
          >
            Ajouter l'email
          </Button>
        </div>
     
      </form>
    );
  }

  // Step 3: Profile
  return (
    <form onSubmit={handleFinalSubmit} className="auth-step-form">
      <div className="form-row">
        <div className="form-group-auth">
          <label htmlFor="title">Titre</label>
          <select id="title" value={title} onChange={(e) => setTitle(e.target.value)} className="auth-input">
            <option value="">Sélectionner</option>
            <option value="Dr">Dr</option>
            <option value="Pr">Pr</option>
            <option value="M.">M.</option>
            <option value="Mme">Mme</option>
          </select>
        </div>

        <div className="form-group-auth">
          <label htmlFor="firstName">Prénom</label>
          <input
            id="firstName"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Jean"
            className="auth-input"
            autoComplete="given-name"
            autoFocus
            required
          />
        </div>
      </div>

      <div className="form-group-auth">
        <label htmlFor="lastName">Nom</label>
        <input
          id="lastName"
          type="text"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Dupont"
          className="auth-input"
          autoComplete="family-name"
          required
        />
      </div>

      <div className="form-group-auth">
        <label htmlFor="specialty">Spécialité</label>
        <select id="specialty" value={specialty} onChange={(e) => setSpecialty(e.target.value)} className="auth-input">
          <option value="">Sélectionner</option>
          {SPECIALTIES.map((spec, index) => (
            <option key={index} value={spec}>{spec}</option>
          ))}
        </select>
      </div>

      <div className="form-group-auth" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 16 }}>
        <input
          id="acceptTerms"
          type="checkbox"
          checked={acceptedTerms}
          onChange={(e) => setAcceptedTerms(e.target.checked)}
          style={{ marginTop: 4 }}
        />
        <label htmlFor="acceptTerms" style={{ margin: 0, fontSize: '13px', color: '#666', lineHeight: '1.5' }}>
          J'accepte les{' '}
          <span
            onClick={() => openExternalUrl('https://voxia-md.ch/#/terms')}
            style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
          >
            conditions générales
          </span>
          {' '}et la{' '}
          <span
            onClick={() => openExternalUrl('https://voxia-md.ch/#/privacy')}
            style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
          >
            déclaration de protection des données
          </span>
        </label>
      </div>

      <Button icon={FaUser} htmlType="submit" variant="primary" size="large" disabled={isSubmitting || !acceptedTerms}>
        Créer mon compte
      </Button>
    </form>
  );
}

// PIN recovery step
function RecoverPinStep({ data, onComplete, onBack, setError }) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }

    setIsSubmitting(true);
    try {
      await authManager.requestRecovery(email);
      setEmailSent(true);
    } catch (err) {
      setError(getUserFacingError(err, 'Erreur lors de l\'envoi de l\'email'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (emailSent) {
    return (
      <div className="recovery-sent">
        <div className="success-icon">
          <FaEnvelope />
        </div>
        <h3>Email envoyé</h3>
        <p>
          Un lien de réinitialisation a été envoyé à {email}.
          Vérifiez votre boîte de réception.
        </p>
        <Button
          variant="primary"
          onClick={onBack}
        >
          Retour à la connexion
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="auth-step-form">
      <div className="form-group-auth">
        <label htmlFor="email">Adresse email du compte</label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="votre@email.com"
          className="auth-input"
          autoComplete="email"
          autoFocus
        />
      </div>

      <div className="form-actions">
        <Button
          icon={FaArrowLeft}
          variant="secondary"
          onClick={onBack}
          disabled={isSubmitting}
        >
          Retour
        </Button>
        <Button
          icon={FaEnvelope}
          htmlType="submit"
          variant="primary"
          disabled={isSubmitting}
        >
          Envoyer
        </Button>
      </div>
    </form>
  );
}

// Reset PIN step
function ResetPinStep({ data, onComplete, setError }) {
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const confirmPinRef = React.useRef(null);

  // Auto-focus confirm PIN when first PIN is complete
  useEffect(() => {
    if (newPin.length === 6 && confirmPinRef.current) {
      confirmPinRef.current.focus();
    }
  }, [newPin]);

  // Auto-submit when both PINs are complete and match
  useEffect(() => {
    if (newPin.length === 6 && confirmPin.length === 6 && newPin === confirmPin && !isSubmitting) {
      handleSubmit(new Event('submit'));
    }
  }, [newPin, confirmPin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!newPin || newPin.length !== 6) {
      setError('Le code PIN doit contenir 6 chiffres');
      return;
    }

    if (newPin !== confirmPin) {
      setError('Les codes PIN ne correspondent pas');
      return;
    }

    // Prevent trivial PINs
    const trivial = new Set(['000000','111111','222222','333333','444444','555555','666666','777777','888888','999999','123456','654321']);
    if (trivial.has(newPin)) {
      setError('Le code PIN est trop simple. Veuillez choisir un autre code.');
      return;
    }

    setIsSubmitting(true);
    try {
      await authManager.completeRecovery(data.recoveryToken, newPin);
      window.alert('PIN réinitialisé avec succès');
      // Ensure we land on the auth flow and refresh state
      try {
        navigate('/auth', { replace: true });
        // Give the router a tick, then hard reload to reset any state
        setTimeout(() => {
          window.location.reload();
        }, 10);
      } catch (_) {
        // Fallback: force hash and reload for HashRouter
        window.location.hash = '#/auth';
        window.location.reload();
      }
    } catch (err) {
      setError(getUserFacingError(err, 'Erreur lors de la réinitialisation du PIN'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="auth-step-form">
      <div className="form-group-auth">
        <label htmlFor="newPin">Nouveau code PIN (6 chiffres)</label>
        <input
          id="newPin"
          type="password"
          inputMode="numeric"
          value={newPin}
          onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="••••••"
          className="auth-input auth-input-pin"
          autoComplete="new-password"
          autoFocus
          maxLength={6}
        />
      </div>

      <div className="form-group-auth">
        <label htmlFor="confirmPin">Confirmer le code PIN</label>
        <input
          ref={confirmPinRef}
          id="confirmPin"
          type="password"
          inputMode="numeric"
          value={confirmPin}
          onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="••••••"
          className="auth-input auth-input-pin"
          autoComplete="new-password"
          maxLength={6}
        />
      </div>

      <Button
        icon={FaLock}
        htmlType="submit"
        variant="primary"
        size="large"
        disabled={isSubmitting}
      >
        Réinitialiser le PIN
      </Button>
    </form>
  );
}

export default AuthFlow;